import { useState } from "react";
import { Currency } from "@/gql/graphql";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { DefaultRateRecord, metaQuery } from "@/app/Meta/GraphQL/metaQuery";
import { updateDefaultRateMutation } from "@/app/Meta/GraphQL/defaultRateMutations";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
} from "@/components/catalyst/navbar";

type RateField = keyof Omit<DefaultRateRecord, "id" | "currency">;

const rateFields: { field: RateField; label: string }[] = [
  { field: "booking_fee_amount", label: "Booking fee" },
  { field: "charge_per_move_amount", label: "Charge per move" },
  { field: "holding_deposit_amount", label: "Holding deposit" },
  { field: "hire_unit_rate", label: "Relocation rate" },
  { field: "extra_hire_unit_deposit_rate", label: "Full price deposit" },
];

export function SettingsCurrencyScreen() {
  const [currency, setCurrency] = useState(Currency.Aud);
  const { data } = useSuspenseGqlQuery(metaQuery, {});

  const selectedRate = data.meta.default_rates.find(
    (rate) => rate.currency === currency,
  );

  if (!selectedRate) {
    throw new Error(`Currency ${currency} not found`);
  }

  return (
    <EditableRowsProvider>
      <SettingRowsCard
        title="Default rates"
        description="Applied to new suppliers and vehicles in each currency. Changes don't affect existing suppliers."
      >
        <Navbar className="overflow-x-auto px-4 sm:px-6">
          <NavbarSection>
            {Object.values(Currency).map((item) => (
              <NavbarItem
                key={item}
                current={item === currency}
                onClick={() => setCurrency(item)}
              >
                {item}
              </NavbarItem>
            ))}
          </NavbarSection>
        </Navbar>

        <DefaultRateRows key={selectedRate.id} rate={selectedRate} />
      </SettingRowsCard>
    </EditableRowsProvider>
  );
}

function DefaultRateRows({ rate }: { rate: DefaultRateRecord }) {
  const { mutateAsync } = useGqlMutation(updateDefaultRateMutation);

  return rateFields.map(({ field, label }) => (
    <EditableSettingRow
      key={field}
      label={label}
      value={formatCurrency(rate[field], rate.currency)}
      initialValues={{ [field]: rate[field] }}
      onSave={(values) => mutateAsync({ id: rate.id, input: values })}
    >
      <CurrencyInput currency={rate.currency} name={field} label={label} />
    </EditableSettingRow>
  ));
}
